import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useMemo } from 'react'

const CrossMatchingTable = ({ matchingData, talents }) => {
  const columnHelper = createColumnHelper()

  // Extract careers and talent names from matching data
  const careersFromData = useMemo(() => {
    if (!matchingData) return []

    const careerMap = new Map()

    Object.values(matchingData).forEach((talentData) => {
      Object.entries(talentData).forEach(([careerId, careerArray]) => {
        if (careerArray && careerArray[0]) {
          const careerInfo = careerArray[0]
          careerMap.set(careerId, {
            id: careerId,
            title: careerInfo.title || `Career ${careerId}`,
            ...careerInfo
          })
        }
      })
    })

    return Array.from(careerMap.values())
  }, [matchingData])

  // Extract talent names from matching data
  const talentNamesFromData = useMemo(() => {
    if (!matchingData) return {}

    console.log('🔍 Extracting talent names from data...')

    const names = {}
    Object.entries(matchingData).forEach(([talentId, talentData]) => {
      console.log(`📝 Processing talent ${talentId}:`, talentData)

      // Check each career entry for person_name
      Object.entries(talentData).forEach(([careerId, careerArray]) => {
        if (careerArray && Array.isArray(careerArray)) {
          careerArray.forEach((careerEntry, index) => {
            console.log(`   Career ${careerId}[${index}]:`, {
              person_name: careerEntry?.person_name,
              title: careerEntry?.title,
              matching_score_to_career: careerEntry?.matching_score_to_career
            })

            if (careerEntry?.person_name && !names[talentId]) {
              names[talentId] = careerEntry.person_name
              console.log(
                `✅ Found person_name for ${talentId}: ${careerEntry.person_name}`
              )
            }
          })
        }
      })

      if (!names[talentId]) {
        console.log(`❌ No person_name found for talent ${talentId}`)
      }
    })

    console.log('📊 Final talent names:', names)
    return names
  }, [matchingData])

  // Transform data for table structure
  const tableData = useMemo(() => {
    if (!matchingData || !talents) return []

    console.log('matchingData', matchingData)

    return Object.keys(matchingData).map((talentId) => {
      const talentData = matchingData[talentId]

      console.log('talentData', JSON.stringify(talentData, null, 2))

      // TODO: CHECK IF CORRECT DATA STRUCTURE

      const talent = talents.find((t) => t.id === talentId)

      const row = {
        talentId,
        talentName: talentNamesFromData[talentId] || 'Unknown Talent',
        ...Object.keys(talentData).reduce((acc, careerId) => {
          const careerData = talentData[careerId]

          // Check all entries in the career array for matching score
          let matchingScore = 0
          if (careerData && Array.isArray(careerData)) {
            for (const entry of careerData) {
              if (
                entry?.matching_score_to_career &&
                entry.matching_score_to_career !== null
              ) {
                matchingScore = entry.matching_score_to_career
                break
              }
            }
          }

          console.log(
            `💯 Matching score for ${talentId} -> ${careerId}: ${matchingScore}`
          )
          acc[careerId] = matchingScore
          return acc
        }, {})
      }

      console.log(`🎯 Final row for ${talentId}:`, {
        talentId: row.talentId,
        talentName: row.talentName,
        fromData: talentNamesFromData[talentId],
        fromTalent: talent?.name,
        fromFirstLastName: talent?.firstName + ' ' + talent?.lastName
      })

      return row
    })
  }, [matchingData, talents, talentNamesFromData])

  // Create columns dynamically
  const columns = useMemo(() => {
    if (!careersFromData || careersFromData.length === 0) return []

    console.log('CAREERS FROM DATA @@@', careersFromData)

    const baseColumns = [
      columnHelper.accessor('talentName', {
        header: 'Talent',
        cell: (info) => (
          <div className='font-medium text-gray-900'>{info.getValue()}</div>
        ),
        size: 200,
        minSize: 150
      })
    ]

    const careerColumns = careersFromData.map((career) =>
      columnHelper.accessor(career.id, {
        header: career.title || career.name || 'Career',
        cell: (info) => {
          const score = info.getValue()
          return (
            <div className='text-center'>
              {score ? (
                <div
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    score >= 80
                      ? 'bg-green-100 text-green-800'
                      : score >= 60
                        ? 'bg-yellow-100 text-yellow-800'
                        : score >= 40
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-red-100 text-red-800'
                  }`}
                >
                  {Math.round(score)}%
                </div>
              ) : (
                <span className='text-gray-400'>-</span>
              )}
            </div>
          )
        },
        size: 120,
        minSize: 100
      })
    )

    return [...baseColumns, ...careerColumns]
  }, [careersFromData, columnHelper])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  if (!matchingData || !talents) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='text-gray-500'>Loading matching data...</div>
      </div>
    )
  }

  return (
    <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-300'>
          <thead className='bg-gray-50'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white'>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className='hover:bg-gray-50'>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className='whitespace-nowrap px-6 py-4 text-sm'
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tableData.length === 0 && (
        <div className='py-8 text-center'>
          <div className='text-gray-500'>No matching data available</div>
        </div>
      )}
    </div>
  )
}

export default CrossMatchingTable
